// apiUtils.ts - Completely revised with aggressive throttling and request management

import axios, { type AxiosRequestConfig } from "axios"
import axiosInstance from "../CustomAxios/axiosInstance"
// Define API URL from environment with fallback
export const API_URL = process.env.REACT_APP_API_URL

// Global request queue and rate limiting
class RequestManager {
    private static instance: RequestManager
    private queue: Array<{
        id: string
        config: AxiosRequestConfig
        resolve: (value: any) => void
        reject: (reason: any) => void
        priority: number
        endpoint: string
        retryCount: number
    }> = []
    private processing = false
    private endpointTimestamps: Record<string, number> = {}
    private endpointCooldowns: Record<string, number> = {}

    // Minimum time between requests (ms)
    private MIN_REQUEST_INTERVAL = 100 // Reduced from 300ms

    // Endpoint-specific rate limits
    private ENDPOINT_LIMITS: Record<string, { maxRequests: number; timeWindow: number }> = {
        "/orders": { maxRequests: 5, timeWindow: 2000 },
        "/entities/brands": { maxRequests: 3, timeWindow: 2000 },
        "/entities/categories": { maxRequests: 3, timeWindow: 2000 },
        "/store/store_header": { maxRequests: 3, timeWindow: 2000 },
        "/products/stock/summary": { maxRequests: 3, timeWindow: 2000 },
        "/stock/statistics": { maxRequests: 5, timeWindow: 2000 },
        "/entities/categories/summary": { maxRequests: 5, timeWindow: 2000 },
        "/entities/subcategories/getSubcategoriesProductsAndOrders": { maxRequests: 5, timeWindow: 2000 },
        "/orders/peak-hour": { maxRequests: 5, timeWindow: 2000 },
        "/compare-earnings": { maxRequests: 5, timeWindow: 2000 },
        default: { maxRequests: 5, timeWindow: 2000 },
    }

    // Cooldown periods after rate limit is hit (ms)
    private COOLDOWN_PERIOD = 2000 // Reduced from 5000ms
    private COOLDOWN_MULTIPLIER = 1.5 // Reduced from 2

    // Maximum retries for rate-limited requests
    private MAX_RETRIES = 3

    private constructor() {}

    public static getInstance(): RequestManager {
        if (!RequestManager.instance) {
            RequestManager.instance = new RequestManager()
        }
        return RequestManager.instance
    }

    // Extract endpoint path from URL for rate limiting
    private getEndpointPath(url: string): string {
        try {
            const parsedUrl = new URL(url)
            const path = parsedUrl.pathname

            // Match to known endpoints
            for (const endpoint of Object.keys(this.ENDPOINT_LIMITS)) {
                if (path.includes(endpoint)) {
                    return endpoint
                }
            }
            return "default"
        } catch (e) {
            return "default"
        }
    }

    // Check if an endpoint is rate-limited
    private isRateLimited(endpoint: string): boolean {
        // Check if endpoint is on cooldown
        if (this.endpointCooldowns[endpoint] && Date.now() < this.endpointCooldowns[endpoint]) {
            return true
        }

        // Get rate limit settings for this endpoint
        const limits = this.ENDPOINT_LIMITS[endpoint] || this.ENDPOINT_LIMITS.default

        // Count recent requests within the time window
        const now = Date.now()
        const recentRequests = Object.entries(this.endpointTimestamps).filter(
            ([key, timestamp]) => key.startsWith(endpoint) && now - timestamp < limits.timeWindow,
        ).length

        return recentRequests >= limits.maxRequests
    }

    // Set cooldown for an endpoint after rate limit is hit
    private setCooldown(endpoint: string): void {
        const currentCooldown = this.endpointCooldowns[endpoint]
        const now = Date.now()

        // If already on cooldown, increase the duration
        if (currentCooldown && currentCooldown > now) {
            const remainingCooldown = currentCooldown - now
            this.endpointCooldowns[endpoint] = now + remainingCooldown * this.COOLDOWN_MULTIPLIER
        } else {
            // Set initial cooldown
            this.endpointCooldowns[endpoint] = now + this.COOLDOWN_PERIOD
        }

        console.warn(
            `Endpoint ${endpoint} on cooldown until ${new Date(this.endpointCooldowns[endpoint]).toLocaleTimeString()}`,
        )
    }

    // Add a request to the queue
    public enqueue(config: AxiosRequestConfig, priority = 1): Promise<any> {
        const endpoint = this.getEndpointPath(config.url || "")
        const id = `${config.method || "GET"}-${config.url}-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`

        return new Promise((resolve, reject) => {
            this.queue.push({
                id,
                config,
                resolve,
                reject,
                priority,
                endpoint,
                retryCount: 0,
            })

            // Sort queue by priority (higher first)
            this.queue.sort((a, b) => b.priority - a.priority)

            // Start processing if not already running
            if (!this.processing) {
                this.processQueue()
            }
        })
    }

    // Process the request queue
    private async processQueue(): Promise<void> {
        if (this.queue.length === 0) {
            this.processing = false
            return
        }

        this.processing = true

        // Find the first request that isn't rate limited
        const requestIndex = this.queue.findIndex((req) => !this.isRateLimited(req.endpoint))

        if (requestIndex === -1) {
            // All endpoints are rate limited, wait and try again
                        await new Promise((resolve) => setTimeout(resolve, 500))
            return this.processQueue()
        }

        const request = this.queue[requestIndex]
        this.queue.splice(requestIndex, 1)

        try {
            // Record the request timestamp for rate limiting
            const requestId = `${request.endpoint}-${Date.now()}`
            this.endpointTimestamps[requestId] = Date.now()

            // Clean up old timestamp entries
            this.cleanupTimestamps()

            // Execute the request using your axios instance
            const response = await axiosInstance(request.config)
            request.resolve(response.data)

            // Wait before processing next request
            await new Promise((resolve) => setTimeout(resolve, this.MIN_REQUEST_INTERVAL))
            this.processQueue()
        } catch (error: any) {
            // Handle rate limit errors
            if (axios.isAxiosError(error) && error.response?.status === 429) {
                console.warn(`Rate limit hit for ${request.endpoint}. Retry count: ${request.retryCount}`)

                // Set cooldown for this endpoint
                this.setCooldown(request.endpoint)

                // Retry the request if under max retries
                if (request.retryCount < this.MAX_RETRIES) {
                    // Increase retry count and re-add to queue with higher priority
                    request.retryCount++
                    request.priority += 1 // Increase priority for retries

                    // Add exponential backoff delay
                    const backoffDelay = Math.min(1000 * Math.pow(2, request.retryCount), 10000)
                    
                    setTimeout(() => {
                        this.queue.push(request)
                        this.queue.sort((a, b) => b.priority - a.priority)
                    }, backoffDelay)
                } else {
                    // Max retries exceeded
                    request.reject(new Error(`Rate limit exceeded for ${request.endpoint} after ${this.MAX_RETRIES} retries`))
                }
            } else {
                // Other errors
                request.reject(error)
            }

            // Continue processing queue
            setTimeout(() => this.processQueue(), this.MIN_REQUEST_INTERVAL)
        }
    }

    // Clean up old timestamp entries to prevent memory leaks
    private cleanupTimestamps(): void {
        const now = Date.now()
        const maxWindow = Math.max(...Object.values(this.ENDPOINT_LIMITS).map((limit) => limit.timeWindow))

        Object.keys(this.endpointTimestamps).forEach((key) => {
            if (now - this.endpointTimestamps[key] > maxWindow * 2) {
                delete this.endpointTimestamps[key]
            }
        })
    }

    // Reset all rate limiting state (useful after login)
    public reset(): void {
        this.endpointTimestamps = {}
        this.endpointCooldowns = {}
    }
}

// Cache implementation with TTL
class ApiCache {
    private static instance: ApiCache
    private cache: Map<string, { data: any; timestamp: number }> = new Map()
    private readonly DEFAULT_TTL = 300000 // 5 minutes default
    private readonly EXTENDED_TTL = 900000 // 15 minutes for expensive queries

    // Endpoints that should use extended cache TTL
    private readonly EXTENDED_CACHE_ENDPOINTS = [
        "/entities/brands",
        "/entities/categories",
        "/store/store_header",
        "/entities/products/stock/summary",
        "/stock/statistics",
        "/entities/categories/summary",
        "/entities/subcategories/getSubcategoriesProductsAndOrders",
        "/orders/peak-hour",
        "/compare-earnings"
    ]

    private constructor() {}

    public static getInstance(): ApiCache {
        if (!ApiCache.instance) {
            ApiCache.instance = new ApiCache()
        }
        return ApiCache.instance
    }

    private getTtl(key: string): number {
        // Use extended TTL for certain endpoints
        for (const endpoint of this.EXTENDED_CACHE_ENDPOINTS) {
            if (key.includes(endpoint)) {
                return this.EXTENDED_TTL
            }
        }
        return this.DEFAULT_TTL
    }

    public get(key: string): any | null {
        const cached = this.cache.get(key)
        if (!cached) return null

        const ttl = this.getTtl(key)

        // Check if expired
        if (Date.now() - cached.timestamp > ttl) {
            this.cache.delete(key)
            return null
        }

        return cached.data
    }

    public set(key: string, data: any): void {
        this.cache.set(key, {
            data,
            timestamp: Date.now(),
        })
    }

    public delete(key: string): void {
        this.cache.delete(key)
    }

    public clear(): void {
        this.cache.clear()
    }
}

// Get instances of our managers
const requestManager = RequestManager.getInstance()
const apiCache = ApiCache.getInstance()

// Exported API methods
export const apiService = {
    /**
     * Make a GET request with caching and throttling
     */
    async get<T = any>(url: string, config?: AxiosRequestConfig): Promise<T> {
        // Generate cache key
        const cacheKey = `GET:${url}:${JSON.stringify(config)}`

        try {
            // Check cache first
            const cached = apiCache.get(cacheKey)
            if (cached) {
                                return cached
            }

            // Add timestamp to ensure fresh data
            const timestampedUrl = `${url}${url.includes("?") ? "&" : "?"}_t=${new Date().getTime()}`

            // Use request manager to handle throttling
            const response = await requestManager.enqueue({
                ...config,
                method: "GET",
                url: timestampedUrl,
            })

            // Cache the response
            apiCache.set(cacheKey, response)

            return response
        } catch (error: any) {
            // If it's a 404 error for entities like brands or categories,
            // return a structured error that can be handled gracefully
            if (axios.isAxiosError(error) && error.response?.status === 404) {
                const urlPath = error.config?.url || ""

                if (urlPath.includes("/entities/brands/") || urlPath.includes("/entities/categories/")) {
                    // Extract the entity type and ID from the URL
                    const isCategory = urlPath.includes("categories")
                    const entityType = isCategory ? "Category" : "Brand"

                    // Create a structured error response
                    const errorResponse = {
                        status: "error",
                        message: `${entityType} not found`,
                        data: null,
                    }

                    // Cache this error response to prevent future requests
                    apiCache.set(cacheKey, errorResponse)

                    throw new Error(`${entityType} not found`)
                }
            }

                        throw error
        }
    },

    /**
     * Make a POST request
     */
    async post<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
        try {
            const response = await requestManager.enqueue({
                ...config,
                method: "POST",
                url,
                data,
            })

            return response
        } catch (error) {
                        throw error
        }
    },

    /**
     * Make a PUT request
     */
    async put<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
        try {
            const response = await requestManager.enqueue({
                ...config,
                method: "PUT",
                url,
                data,
            })

            return response
        } catch (error) {
                        throw error
        }
    },

    /**
     * Make a DELETE request
     */
    async delete<T = any>(url: string, config?: AxiosRequestConfig): Promise<T> {
        try {
            const response = await requestManager.enqueue({
                ...config,
                method: "DELETE",
                url,
            })

            return response
        } catch (error) {
                        throw error
        }
    },

    /**
     * Clear the API cache
     */
    clearCache(): void {
        apiCache.clear()
            },

    /**
     * Reset rate limiting state
     */
    resetRateLimits(): void {
        requestManager.reset()
            },
}

export default apiService